import { Controller, useFormContext } from 'react-hook-form';
import {
  Autocomplete, Paper, Popper, SxProps, Theme,
} from '@mui/material';
import { useState } from 'react';
import { IsearchAddressResult } from '@optimus/models';
import { useTranslation } from 'react-i18next';
import {
  IBankAccountFormInputs, IFormInputs, IPaperCheckFormInputs, menuPaperProps, StyledTextField,
} from './StyledFormInputs';
import { useAddressAutocomplete } from './useAddressAutocomplete';

const NO_RESULTS_MARKER = 'NO_RESULTS_MARKER';

// Special marker for "no results" option
const NO_RESULTS_OPTION = {
  fullAddress: NO_RESULTS_MARKER, street: '', city: '', state: '', zipcode: '',
} as IsearchAddressResult;

interface AddressAutoCompleteInputProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formFieldName: 'businessInfo.autoAddress' | 'businessInfo.billingAutoAddress' | 'bankAccountInfo.businessAddress' | 'paperCheckInfo.businessAddress' | 'autoAddress';
  placeholderText: string;
  optionalStyles?: SxProps<Theme>;
}
export function AddressAutoCompleteInput({
  formFieldName, placeholderText, optionalStyles,
}: AddressAutoCompleteInputProps) {
  const { t } = useTranslation();
  const { suggestions, fetchSuggestions, isFetchingSuggestions } = useAddressAutocomplete();
  const {
    control, getValues,
  } =
    useFormContext<IFormInputs | IBankAccountFormInputs | IPaperCheckFormInputs | IFormInputs['businessInfo']>();

  const [currentTypedAddressValue, setCurrentTypedAddressValue] = useState((getValues(formFieldName) as IsearchAddressResult)?.fullAddress ?? '');

  const isValidAddress = (value: IsearchAddressResult) => (
    (!!value?.street && (value.street !== '')) &&
    (!!value?.city && (value.city !== '')) &&
    (!!value?.state && (value.state !== '')) &&
    (!!value?.zipcode && (value.zipcode !== ''))
  );

  return (
    <Controller
      name={formFieldName}
      control={control}
      rules={{
        required: true,
        validate: isValidAddress,
      }}
      render={({
        field: {
          onChange, value, ref, ...restField
        },
        fieldState,
      }) => (
        <Autocomplete
          freeSolo
          disablePortal
          options={suggestions}
          filterOptions={(options, state) => {
            const typed = (state.inputValue ?? '').trim();
            const inputDirtyAddress = (typed || ((value as IsearchAddressResult)?.fullAddress ?? '')).toLowerCase();

            if (!inputDirtyAddress) return options.filter(isValidAddress);

            const filtered = options.filter((opt) => {
              if (!isValidAddress(opt as IsearchAddressResult)) return false;
              const optAddress = opt as IsearchAddressResult;
              return (
                (optAddress.fullAddress ?? '').toLowerCase().includes(inputDirtyAddress) ||
                (optAddress.city ?? '').toLowerCase().includes(inputDirtyAddress) ||
                (optAddress.state ?? '').toLowerCase().includes(inputDirtyAddress) ||
                String(optAddress.zipcode ?? '').toLowerCase().includes(inputDirtyAddress)
              );
            });

            // Only show "no results" if user has actually typed something new (not just prefilled)
            const hasTyped = currentTypedAddressValue.trim();
            if (filtered.length === 0 && hasTyped && !isFetchingSuggestions) {
              return [NO_RESULTS_OPTION];
            }

            return filtered;
          }}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            const opt = option as IsearchAddressResult;
            if (opt.fullAddress === NO_RESULTS_MARKER) {
              return t('error.addressField.noResults');
            }
            return opt.fullAddress;
          }}
          getOptionDisabled={(option) => {
            const opt = option as IsearchAddressResult;
            return opt.fullAddress === NO_RESULTS_MARKER;
          }}
          loading={isFetchingSuggestions}
          openOnFocus
          onOpen={() => {
            const typed = (currentTypedAddressValue ?? '').trim();
            const fallback = ((value as IsearchAddressResult)?.fullAddress ?? '').trim(); // use fullAddress behind the scenes
            fetchSuggestions(typed || fallback);
          }}
          onInputChange={(_, inputValue) => {
            setCurrentTypedAddressValue(inputValue); // Update local state during typing
            fetchSuggestions(inputValue);
          }}
          onChange={(_, newValue) => {
            // Clear local typed value
            setCurrentTypedAddressValue('');
            // If the user clears the input, set the value to empty string or null
            if (newValue === null || newValue === '') {
              onChange(''); // Clear the field value in the form
            } else {
              onChange(newValue);
            }
          }}
          onClose={(_, reason) => {
            // check if an option was selected when click / blur / escape / toggle
            if (reason === 'blur' || reason === 'escape' || reason === 'toggleInput') {
              const typed = (currentTypedAddressValue ?? '').trim();
              const current = getValues(formFieldName) as IsearchAddressResult;
              if (typed !== current?.fullAddress) {
                onChange(typed);
              }
            }
          }}
          renderOption={(props, option) => {
            const opt = option as IsearchAddressResult;
            if (opt.fullAddress === NO_RESULTS_MARKER) {
              return (
                <li {...props} key="no-results" style={{ opacity: 0.6, cursor: 'default' }}>
                  {t('error.addressField.noResults')}
                </li>
              );
            }
            return (
              <li {...props} key={`${opt.fullAddress}-${opt.zipcode}`}>{opt.fullAddress}</li>
            );
          }}
          PaperComponent={({ children }) => (
            <Paper sx={menuPaperProps}>
              {children}
            </Paper>
          )}
          PopperComponent={(props) => {
            const shouldShowPopper = suggestions.length > 0 ||
              isFetchingSuggestions ||
              (currentTypedAddressValue.trim() && !isFetchingSuggestions);

            return shouldShowPopper ?
              <Popper
                {...props}
                modifiers={[
                  {
                    name: 'preventOverflow',
                    options: { boundary: 'window' },
                  },
                  {
                    name: 'flip',
                    options: { fallbackPlacements: [] },
                  },
                ]} placeholder=''
                sx={{
                  width: props.anchorEl instanceof HTMLElement ? props.anchorEl.clientWidth : 'auto',
                }}
              /> : null;
          }}
          value={isValidAddress(value as IsearchAddressResult) ? value : ''}
          inputValue={currentTypedAddressValue || (value as IsearchAddressResult)?.validAddress || ''}
          renderInput={(params) => (
            <StyledTextField
              sx={optionalStyles}
              {...params}
              placeholder={placeholderText}
              error={!!fieldState.error}
              {...restField}
            />
          )}
        />
      )}
    />
  );
}
